/**
 * What the add-in did in the document on the agent's behalf, and how to take
 * it back.
 *
 * Office.js has no Undo, so "undo what you just did" is ours to keep: every
 * agent action that changes the document leaves a journal entry in the
 * document's settings with enough to reverse it. An edit batch or a table
 * change is reversed by rejecting the mytender.io revisions it wrote (they
 * carry the stamp date, so the ones from that batch are the ones dated after
 * it started). A template apply is reversed by removing the frame it inserted
 * and putting back the styles, headers, footers and page setup captured
 * before it ran. The user's own typing, changes they have accepted, and
 * inserted images are not ours to reverse; Word's Undo still covers those.
 */
import { REVISION_AUTHOR } from "./authoredRevision";
import type { StyleSnapshotEntry } from "./identity";
import { importStyleEntries } from "./templateStyles";
import { withTrackingOff } from "./tracking";

export const JOURNAL_SETTING = "mytender_action_journal";
const JOURNAL_LIMIT = 20;
/** Template entries carry header OOXML; keep only the last few to stay within settings limits. */
const TEMPLATE_ENTRY_LIMIT = 3;
/** Revisions are stamped after the batch starts; allow a little clock slack. */
const STAMP_SLACK_MS = 3000;

export type HeaderSnapshot = {
  section: number;
  primaryHeader?: string;
  primaryFooter?: string;
  firstHeader?: string;
  firstFooter?: string;
  evenHeader?: string;
  evenFooter?: string;
};

export type PageSetupSnapshot = {
  topMargin?: number;
  bottomMargin?: number;
  leftMargin?: number;
  rightMargin?: number;
  headerDistance?: number;
  footerDistance?: number;
  orientation?: string;
  pageWidth?: number;
  pageHeight?: number;
  differentFirstPageHeaderFooter?: boolean;
  oddAndEvenPagesHeaderFooter?: boolean;
};

export type TemplateUndo = {
  frameTag: string;
  stylesBefore: StyleSnapshotEntry[];
  headersBefore: HeaderSnapshot[];
  pageSetupBefore: PageSetupSnapshot | null;
  themeAttempted: boolean;
};

export type JournalEntry = {
  id: string;
  kind: "edit" | "template";
  /** ISO time the action started. */
  at: string;
  summary: string;
  template?: TemplateUndo;
};

export type UndoOutcome = {
  status: "ok" | "nothing";
  undone: { kind: JournalEntry["kind"]; summary: string; detail: string }[];
  remaining: number;
  notes: string[];
};

const settingsAvailable = (): boolean => {
  try {
    return Boolean(Office?.context?.document?.settings);
  } catch {
    return false;
  }
};

const saveSettings = (): Promise<void> =>
  new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
      else reject(new Error(result.error?.message ?? "Could not save document settings"));
    });
  });

let memoryJournal: JournalEntry[] = [];

export const loadJournal = (): JournalEntry[] => {
  if (!settingsAvailable()) return memoryJournal;
  try {
    const raw = Office.context.document.settings.get(JOURNAL_SETTING) as string | null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as JournalEntry[]) : [];
  } catch {
    return [];
  }
};

const saveJournal = async (entries: JournalEntry[]): Promise<void> => {
  // Newest last. Trim old entries, and old template entries first: they are
  // the heavy ones (header OOXML) and settings travel inside the .docx.
  let kept = entries.slice(-JOURNAL_LIMIT);
  const templates = kept.filter((e) => e.kind === "template");
  if (templates.length > TEMPLATE_ENTRY_LIMIT) {
    const drop = new Set(templates.slice(0, templates.length - TEMPLATE_ENTRY_LIMIT).map((e) => e.id));
    kept = kept.filter((e) => !drop.has(e.id));
  }
  if (!settingsAvailable()) {
    memoryJournal = kept;
    return;
  }
  const settings = Office.context.document.settings;
  if (kept.length) settings.set(JOURNAL_SETTING, JSON.stringify(kept));
  else settings.remove(JOURNAL_SETTING);
  await saveSettings();
};

/** Record an action that changed the document. Never throws: the action itself already succeeded. */
export const recordAction = async (entry: Omit<JournalEntry, "id">): Promise<void> => {
  try {
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    await saveJournal([...loadJournal(), { ...entry, id }]);
  } catch (error) {
    console.warn("Could not record the action for undo", error);
  }
};

/** Reject the mytender.io revisions written since `since` (an edit batch or a table change). */
const rejectRevisionsSince = async (since: string): Promise<number> => {
  const floor = new Date(since).getTime() - STAMP_SLACK_MS;
  return Word.run(async (context) => {
    const changes = context.document.body.getTrackedChanges();
    changes.load("items/author,items/date,items/type");
    await context.sync();
    const targets = changes.items.filter((change) => {
      if (change.author !== REVISION_AUTHOR) return false;
      const when = change.date ? new Date(change.date).getTime() : Number.NaN;
      return Number.isNaN(when) ? true : when >= floor;
    });
    for (const change of targets) change.reject();
    await context.sync();
    return targets.length;
  });
};

const restoreHeaders = async (headers: HeaderSnapshot[]): Promise<number> =>
  Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    let restored = 0;
    for (const snap of headers) {
      const section = sections.items[snap.section];
      if (!section) continue;
      if (snap.primaryHeader) section.getHeader("Primary").insertOoxml(snap.primaryHeader, "Replace");
      if (snap.primaryFooter) section.getFooter("Primary").insertOoxml(snap.primaryFooter, "Replace");
      if (snap.firstHeader) section.getHeader("FirstPage").insertOoxml(snap.firstHeader, "Replace");
      if (snap.firstFooter) section.getFooter("FirstPage").insertOoxml(snap.firstFooter, "Replace");
      if (snap.evenHeader) section.getHeader("EvenPages").insertOoxml(snap.evenHeader, "Replace");
      if (snap.evenFooter) section.getFooter("EvenPages").insertOoxml(snap.evenFooter, "Replace");
      restored += 1;
    }
    await context.sync();
    return restored;
  });

const restorePageSetup = async (setup: PageSetupSnapshot): Promise<void> =>
  Word.run(async (context) => {
    const page = context.document.pageSetup;
    if (typeof setup.topMargin === "number") page.topMargin = setup.topMargin;
    if (typeof setup.bottomMargin === "number") page.bottomMargin = setup.bottomMargin;
    if (typeof setup.leftMargin === "number") page.leftMargin = setup.leftMargin;
    if (typeof setup.rightMargin === "number") page.rightMargin = setup.rightMargin;
    if (typeof setup.headerDistance === "number") page.headerDistance = setup.headerDistance;
    if (typeof setup.footerDistance === "number") page.footerDistance = setup.footerDistance;
    if (setup.orientation) page.orientation = setup.orientation as Word.PageOrientation;
    if (typeof setup.pageWidth === "number") page.pageWidth = setup.pageWidth;
    if (typeof setup.pageHeight === "number") page.pageHeight = setup.pageHeight;
    if (typeof setup.differentFirstPageHeaderFooter === "boolean")
      page.differentFirstPageHeaderFooter = setup.differentFirstPageHeaderFooter;
    if (typeof setup.oddAndEvenPagesHeaderFooter === "boolean")
      page.oddAndEvenPagesHeaderFooter = setup.oddAndEvenPagesHeaderFooter;
    await context.sync();
  });

const removeFrame = async (tag: string): Promise<number> =>
  Word.run(async (context) => {
    const controls = context.document.body.contentControls.getByTag(tag);
    controls.load("items");
    await context.sync();
    // eslint-disable-next-line office-addins/load-object-before-read -- items are loaded above
    const count = controls.items.length;
    for (const control of controls.items) control.delete(false);
    await context.sync();
    return count;
  });

const undoTemplate = async (undo: TemplateUndo): Promise<{ detail: string; notes: string[] }> =>
  withTrackingOff(async () => {
    const parts: string[] = [];
    const notes: string[] = [];
    const frames = await removeFrame(undo.frameTag).catch(() => 0);
    if (frames) parts.push(`removed ${frames} inserted page block${frames === 1 ? "" : "s"}`);
    if (undo.stylesBefore.length) {
      try {
        await importStyleEntries(undo.stylesBefore);
        parts.push(`put ${undo.stylesBefore.length} styles back`);
      } catch (error) {
        notes.push(`Styles could not be put back: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    if (undo.headersBefore.length) {
      try {
        const n = await restoreHeaders(undo.headersBefore);
        parts.push(`restored headers and footers in ${n} section${n === 1 ? "" : "s"}`);
      } catch (error) {
        notes.push(`Headers could not be put back: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    if (undo.pageSetupBefore) {
      try {
        await restorePageSetup(undo.pageSetupBefore);
        parts.push("restored page setup");
      } catch (error) {
        notes.push(`Page setup could not be put back: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
    if (undo.themeAttempted) notes.push("A theme offered to Word cannot be taken back from the add-in.");
    return { detail: parts.join(", ") || "nothing to reverse", notes };
  });

/**
 * Reverse the last `steps` journaled actions, newest first.
 * @param steps - How many actions to reverse.
 */
export const undoLastChange = async (steps: number): Promise<UndoOutcome> => {
  const journal = loadJournal();
  if (!journal.length) {
    return {
      status: "nothing",
      undone: [],
      remaining: 0,
      notes: ["The add-in has no actions of its own left to reverse in this document."],
    };
  }
  const undone: UndoOutcome["undone"] = [];
  const notes: string[] = [];
  let remaining = [...journal];
  for (let i = 0; i < Math.max(1, steps) && remaining.length; i += 1) {
    const entry = remaining[remaining.length - 1];
    try {
      if (entry.kind === "template" && entry.template) {
        const out = await undoTemplate(entry.template);
        undone.push({ kind: entry.kind, summary: entry.summary, detail: out.detail });
        notes.push(...out.notes);
      } else {
        const n = await rejectRevisionsSince(entry.at);
        undone.push({
          kind: entry.kind,
          summary: entry.summary,
          detail: n ? `rejected ${n} revision${n === 1 ? "" : "s"}` : "its revisions were already accepted or rejected",
        });
      }
      remaining = remaining.slice(0, -1);
    } catch (error) {
      notes.push(`${entry.summary}: ${error instanceof Error ? error.message : String(error)}`);
      break;
    }
  }
  await saveJournal(remaining).catch(() => undefined);
  return { status: undone.length ? "ok" : "nothing", undone, remaining: remaining.length, notes };
};
